import { Box, Text, Input, Textarea, Flex } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';

import {
  getSelectedPage,
  updatePage,
  getSelectedDashboardScenario,
  getCompareDashboardScenario,
  getTimeFilter,
} from 'redux/DashboardSliceV2';
import { AppDispatch } from 'utils/GlobalHelpers';
import { font } from 'styles/revampTokens';

import LiveSyncBadge from './LiveSyncBadge';

interface Properties {
  onRefresh?: () => void;
}

const PageInfo = ({ onRefresh }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const selectedPage = useSelector(getSelectedPage);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const compareScenario = useSelector(getCompareDashboardScenario);
  // Same source the calendar dropdown's trigger reads, so this line always
  // reflects exactly the period currently selected there.
  const timeFilter = useSelector(getTimeFilter);
  const dateRangeLabel =
    timeFilter?.start && timeFilter?.end
      ? `${timeFilter.start.replace('-', ' ')} to ${timeFilter.end.replace('-', ' ')}`
      : '';

  const handleNameDoubleClick = () => {
    setEditedName(selectedPage?.name || '');
    setIsEditingName(true);
  };

  const handleDescriptionDoubleClick = () => {
    setEditedDescription(selectedPage?.description || '');
    setIsEditingDescription(true);
  };

  const handleNameSave = async () => {
    if (selectedPage && editedName.trim()) {
      try {
        await dispatch(
          updatePage({
            request_body: {
              ...selectedPage,
              name: editedName.trim(),
            },
          }),
        );
      } catch {
        // Handle error if needed
      }
    }
    setIsEditingName(false);
  };

  const handleDescriptionSave = async () => {
    if (selectedPage) {
      try {
        await dispatch(
          updatePage({
            request_body: {
              ...selectedPage,
              description: editedDescription.trim(),
            },
          }),
        );
      } catch {
        // Handle error if needed
      }
    }
    setIsEditingDescription(false);
  };

  const handleNameKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleNameSave();
    } else if (event.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const handleDescriptionKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      handleDescriptionSave();
    } else if (event.key === 'Escape') {
      setIsEditingDescription(false);
    }
  };

  return (
    <Box width='100%' mb='12px'>
      {/* Title row: page name lines up on the same baseline as the sync badge */}
      <Flex justifyContent='space-between' alignItems='center' width='100%' gap={2}>
        <Box flex='1' minW='0'>
          {isEditingName ? (
            <Input
              value={editedName}
              onChange={(event) => setEditedName(event.target.value)}
              onBlur={handleNameSave}
              onKeyDown={handleNameKeyPress}
              fontSize='xl'
              fontWeight='700'
              fontFamily={font.display}
              color='blockOverview.textDark'
              bg='blockOverview.white'
              border='1px solid'
              borderColor='blockOverview.border'
              _focus={{
                borderColor: 'blockOverview.primary',
                boxShadow: 'none',
              }}
              autoFocus
            />
          ) : (
            <Text
              fontSize='xl'
              fontWeight='700'
              fontFamily={font.display}
              color='blockOverview.textDark'
              mb={0}
              cursor='pointer'
              onDoubleClick={handleNameDoubleClick}
              _hover={{ opacity: 0.8 }}
              title={selectedPage?.name || 'No name available.'}
              noOfLines={1}
            >
              {selectedPage?.name || 'No name available.'}
            </Text>
          )}
        </Box>
        <LiveSyncBadge onRefresh={onRefresh} />
      </Flex>

      {(selectedPage?.description || isEditingDescription) && (
        <Box>
          {isEditingDescription ? (
            <Textarea
              value={editedDescription}
              onChange={(event) => setEditedDescription(event.target.value)}
              onBlur={handleDescriptionSave}
              onKeyDown={handleDescriptionKeyPress}
              fontSize='md'
              color='blockOverview.textDark'
              bg='blockOverview.white'
              border='1px solid'
              borderColor='blockOverview.border'
              _focus={{
                borderColor: 'blockOverview.primary',
                boxShadow: 'none',
              }}
              mb={1}
              autoFocus
              resize='vertical'
              minH='60px'
            />
          ) : (
            <Text
              fontSize='md'
              color='blockOverview.textSecondary'
              mb={1}
              cursor='pointer'
              onDoubleClick={handleDescriptionDoubleClick}
              _hover={{ opacity: 0.8 }}
              title={selectedPage?.description || 'Add description here'}
              noOfLines={1}
            >
              {selectedPage?.description || 'Add description here'}
            </Text>
          )}
        </Box>
      )}

      {/* Compact scenario + reporting period bar */}
      <Flex flexWrap='wrap' gap={1} alignItems='center'>
        <Text
          fontFamily='Roboto Regular'
          fontWeight='500'
          fontSize='13px'
          color='blockOverview.info'
          noOfLines={1}
        >
          {selectedScenario?.name || 'No scenario selected'}
          {compareScenario ? ` vs ${compareScenario.name}` : ''}
        </Text>
        {dateRangeLabel && (
          <>
            <Text
              fontFamily='Roboto Regular'
              fontWeight='500'
              fontSize='13px'
              color='blockOverview.info'
            >
              {'•'}
            </Text>
            <Text
              fontFamily='Roboto Regular'
              fontWeight='500'
              fontSize='13px'
              color='blockOverview.info'
              noOfLines={1}
            >
              {dateRangeLabel}
            </Text>
          </>
        )}
      </Flex>
    </Box>
  );
};
export default PageInfo;
